import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type {
  DebatePhase,
  DebateTranscriptEntry,
  DebateTurnRequest,
} from '@arena/shared';
import { SceneBack } from '@/shared/components/SceneBack';
import { findCharacter, portraitFor } from '../data/characters';
import { streamDebateTurn } from '../lib/debateStream';
import {
  DEFAULT_FORMAT,
  labelForPhase,
  MODERATOR_ID,
  planDebate,
  type PlannedTurn,
} from '../lib/orchestrator';
import './WatchDebate.css';

/**
 * Live debate viewer — /arena/watch/debate.
 *
 * The user has already picked two figures and a topic on /arena/watch. We
 * read those from router state (sessionStorage is a fallback if they hard-
 * refresh). The deterministic orchestrator hands us a turn plan; we walk it
 * one turn at a time, streaming each turn's content from the API and
 * appending to the transcript as it arrives.
 */

interface DebateLocationState {
  leftId?: string;
  rightId?: string;
  topic?: string;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'streaming'; turnIndex: number }
  | { kind: 'paused'; nextTurnIndex: number }
  | { kind: 'complete' }
  | { kind: 'error'; message: string };

const SESSION_KEY = 'arena.watch.lastDebate';

export function WatchDebate() {
  const location = useLocation();
  const navigate = useNavigate();

  // Pull the picked debate out of router state, with a sessionStorage fallback
  // so a hard-refresh on this route doesn't immediately bounce.
  const initial = useMemo<DebateLocationState>(() => {
    const fromState = (location.state ?? {}) as DebateLocationState;
    if (fromState.leftId && fromState.rightId && fromState.topic) {
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(fromState));
      } catch {
        // ignore quota / privacy-mode errors
      }
      return fromState;
    }
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) return JSON.parse(raw) as DebateLocationState;
    } catch {
      // ignore
    }
    return {};
  }, [location.state]);

  const leftId = initial.leftId;
  const rightId = initial.rightId;
  const topic = initial.topic;

  const left = useMemo(() => findCharacter(leftId ?? null), [leftId]);
  const right = useMemo(() => findCharacter(rightId ?? null), [rightId]);

  const plan = useMemo<PlannedTurn[]>(() => {
    if (!leftId || !rightId) return [];
    return planDebate(leftId, rightId, DEFAULT_FORMAT);
  }, [leftId, rightId]);

  const [transcript, setTranscript] = useState<DebateTranscriptEntry[]>([]);
  const [currentText, setCurrentText] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const abortRef = useRef<AbortController | null>(null);
  const transcriptScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = topic
      ? `${topic} — The Arena`
      : 'The Stands — The Arena';
  }, [topic]);

  // Auto-scroll the transcript as new content arrives.
  useEffect(() => {
    const el = transcriptScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [transcript.length, currentText]);

  const runTurn = useCallback(
    async (turnIndex: number) => {
      if (!left || !right || !topic) return;
      const turn = plan[turnIndex];
      if (!turn) {
        setStatus({ kind: 'complete' });
        return;
      }

      const abort = new AbortController();
      abortRef.current = abort;

      setCurrentText('');
      setStatus({ kind: 'streaming', turnIndex });

      const body: DebateTurnRequest = {
        speakerId: turn.speakerId === MODERATOR_ID ? 'moderator' : turn.speakerId,
        topic,
        participants: [
          { id: left.id, name: left.name },
          { id: right.id, name: right.name },
        ],
        phase: turn.phase,
        transcript,
      };

      let accumulated = '';
      try {
        await streamDebateTurn({
          body,
          signal: abort.signal,
          onDelta: (text) => {
            accumulated += text;
            setCurrentText(accumulated);
          },
        });
      } catch (err) {
        if (abort.signal.aborted) return; // intentional cancellation
        const message = err instanceof Error ? err.message : 'Request failed';
        setStatus({ kind: 'error', message });
        return;
      }

      // Commit this turn to the transcript and pause before the next one so
      // the user can read at their own pace. They click "Continue" to advance.
      const completed: DebateTranscriptEntry = {
        speakerId: turn.speakerId === MODERATOR_ID ? 'moderator' : turn.speakerId,
        phase: turn.phase,
        content: accumulated.trim(),
      };
      setTranscript((prev) => [...prev, completed]);
      setCurrentText('');

      const nextIndex = turnIndex + 1;
      if (nextIndex >= plan.length) {
        setStatus({ kind: 'complete' });
      } else {
        setStatus({ kind: 'paused', nextTurnIndex: nextIndex });
      }
    },
    [left, right, topic, plan, transcript],
  );

  // Kick off the first turn once we know the plan + parties.
  const hasStartedRef = useRef(false);
  useEffect(() => {
    if (hasStartedRef.current) return;
    if (!left || !right || !topic || plan.length === 0) return;
    hasStartedRef.current = true;
    void runTurn(0);
    return () => {
      abortRef.current?.abort();
    };
    // runTurn intentionally omitted — we want to start exactly once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [left, right, topic, plan.length]);

  // Group transcript entries by phase so we can show a phase header once,
  // not on every turn — closer to a real transcript layout. Declared here
  // (above the early return below) so the hook count stays stable.
  const groupedTranscript = useMemo(() => {
    const groups: Array<{ phase: DebatePhase; entries: DebateTranscriptEntry[] }> = [];
    for (const entry of transcript) {
      const last = groups[groups.length - 1];
      if (last && last.phase === entry.phase) {
        last.entries.push(entry);
      } else {
        groups.push({ phase: entry.phase, entries: [entry] });
      }
    }
    return groups;
  }, [transcript]);

  // No picked debate (direct nav or stale state) → bounce to setup.
  if (!leftId || !rightId || !topic || !left || !right) {
    return <Navigate to="/arena/watch" replace />;
  }

  const handleContinue = () => {
    if (status.kind !== 'paused') return;
    void runTurn(status.nextTurnIndex);
  };

  const handleRestart = () => {
    abortRef.current?.abort();
    setTranscript([]);
    setCurrentText('');
    hasStartedRef.current = true;
    void runTurn(0);
  };

  const handleNewDebate = () => {
    abortRef.current?.abort();
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // ignore
    }
    navigate('/arena/watch');
  };

  const nameFor = (id: string): string => {
    if (id === 'moderator') return 'The Moderator';
    if (id === left.id) return left.name;
    if (id === right.id) return right.name;
    return 'Speaker';
  };

  const sideFor = (id: string): 'left' | 'right' | 'moderator' => {
    if (id === 'moderator') return 'moderator';
    if (id === left.id) return 'left';
    return 'right';
  };

  const currentTurn =
    status.kind === 'streaming' ? plan[status.turnIndex] : undefined;

  return (
    <main className="scene scene--debate">
      <div
        className="scene__image scene__image--watch"
        role="img"
        aria-label="A roaring crowd packed into the colosseum stands"
      />
      <div className="scene__vignette" />
      <SceneBack />

      <section className="debate">
        <header className="debate__header">
          <p className="debate__eyebrow">— The Stands —</p>
          <p className="debate__topic">&ldquo;{topic}&rdquo;</p>

          <div className="debate__matchup">
            <div className="debate__side debate__side--left">
              <img
                className="debate__portrait"
                src={portraitFor(left.id)}
                alt=""
                loading="eager"
              />
              <div className="debate__side-name">
                <em>{left.name}</em>
                <span className="debate__side-era">{left.era}</span>
              </div>
            </div>
            <span className="debate__vs" aria-hidden="true">
              vs
            </span>
            <div className="debate__side debate__side--right">
              <img
                className="debate__portrait"
                src={portraitFor(right.id)}
                alt=""
                loading="eager"
              />
              <div className="debate__side-name">
                <em>{right.name}</em>
                <span className="debate__side-era">{right.era}</span>
              </div>
            </div>
          </div>
        </header>

        <div className="debate__transcript" ref={transcriptScrollRef}>
          {groupedTranscript.map((group, gi) => (
            <div key={gi} className="debate__phase">
              <p className="debate__phase-label">— {labelForPhase(group.phase)} —</p>
              {group.entries.map((entry, ei) => (
                <article
                  key={`${gi}-${ei}`}
                  className={`debate__turn debate__turn--${sideFor(entry.speakerId)}`}
                >
                  <header className="debate__turn-header">
                    {entry.speakerId !== 'moderator' && (
                      <img
                        className="debate__turn-portrait"
                        src={portraitFor(entry.speakerId)}
                        alt=""
                        loading="lazy"
                      />
                    )}
                    <span className="debate__turn-name">{nameFor(entry.speakerId)}</span>
                  </header>
                  <div className="debate__turn-body">
                    {entry.content.split(/\n\n+/).map((para, pi) => (
                      <p key={pi}>{para}</p>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          ))}

          {currentTurn && (
            <div className="debate__phase">
              {/* Only show a phase label if this turn starts a new phase. */}
              {(() => {
                const lastCommitted = transcript[transcript.length - 1];
                const startsNewPhase =
                  !lastCommitted || lastCommitted.phase !== currentTurn.phase;
                return startsNewPhase ? (
                  <p className="debate__phase-label">
                    — {labelForPhase(currentTurn.phase)} —
                  </p>
                ) : null;
              })()}
              <article
                className={`debate__turn debate__turn--${sideFor(currentTurn.speakerId)} debate__turn--streaming`}
              >
                <header className="debate__turn-header">
                  {currentTurn.speakerId !== MODERATOR_ID && (
                    <img
                      className="debate__turn-portrait"
                      src={portraitFor(currentTurn.speakerId)}
                      alt=""
                      loading="lazy"
                    />
                  )}
                  <span className="debate__turn-name">
                    {nameFor(currentTurn.speakerId)}
                  </span>
                  <span className="debate__turn-status" aria-live="polite">
                    speaking
                    <span className="debate__turn-status-dot" aria-hidden="true" />
                    <span className="debate__turn-status-dot" aria-hidden="true" />
                    <span className="debate__turn-status-dot" aria-hidden="true" />
                  </span>
                </header>
                <div className="debate__turn-body">
                  {currentText.trim().length === 0 ? (
                    <p className="debate__turn-pending">…</p>
                  ) : (
                    currentText
                      .split(/\n\n+/)
                      .map((para, pi) => <p key={pi}>{para}</p>)
                  )}
                </div>
              </article>
            </div>
          )}

          {status.kind === 'error' && (
            <div className="debate__error" role="alert">
              <p>The debate was cut short: {status.message}</p>
              <button type="button" className="debate__btn" onClick={handleRestart}>
                Restart
              </button>
            </div>
          )}
        </div>

        <footer className="debate__controls">
          <div className="debate__controls-left">
            <button
              type="button"
              className="debate__btn debate__btn--ghost"
              onClick={handleNewDebate}
            >
              New debate
            </button>
          </div>
          <div className="debate__controls-right">
            {status.kind === 'paused' && (
              <button type="button" className="debate__btn" onClick={handleContinue}>
                Continue →
              </button>
            )}
            {status.kind === 'streaming' && (
              <span className="debate__progress">
                Turn {status.turnIndex + 1} of {plan.length}
              </span>
            )}
            {status.kind === 'complete' && (
              <span className="debate__progress">The debate is concluded.</span>
            )}
          </div>
        </footer>
      </section>
    </main>
  );
}
